"use client";

import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import CommonIntersectionElement from "@/components/utils/CommonIntersectionElement";
import type { ApiRepContributor } from "@/generated/models/ApiRepContributor";
import type { ApiRepContributorsPage } from "@/generated/models/ApiRepContributorsPage";
import type { ApiRatingWithProfileInfoAndLevel } from "@/generated/models/ApiRatingWithProfileInfoAndLevel";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { formatAddress, formatNumberWithCommas } from "@/helpers/Helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { useInfiniteQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useMemo } from "react";
import { getContributorLabel, type RepDirection } from "./UserPageRep.helpers";
const REP_CONTRIBUTORS_QUERY_KEY = "REP_CONTRIBUTORS";
const REP_CONTRIBUTORS_PAGE_SIZE = 100;
const REP_CONTRIBUTORS_STALE_TIME_MS = 5 * 60 * 1000;
type RepContributorScope = "overview" | "category";
interface RepContributorsDialogProps {
  readonly identity: string;
  readonly isOpen: boolean;
  readonly scope: RepContributorScope;
  readonly direction: RepDirection;
  readonly category?: string | null | undefined;
  readonly contributorCount: number;
  readonly onClose: () => void;
}
interface RepContributorListItem {
  readonly key: string;
  readonly href: string;
  readonly display: string;
  readonly pfpUrl: string | null;
  readonly contribution: number;
}
interface RepContributorPage {
  readonly data: RepContributorListItem[];
  readonly page: number;
  readonly next: boolean;
}
type ApiRatingWithProfileInfoAndLevelWithPfp = ApiRatingWithProfileInfoAndLevel & {
  readonly pfp?: string | null;
};
type ApiRatingWithProfileInfoAndLevelPageWithPfp = Pick<ApiRepContributorsPage, "page" | "next"> & {
  readonly data: ApiRatingWithProfileInfoAndLevelWithPfp[];
};
function getRouteHref(handleOrWallet: string): string {
  return `/${encodeURIComponent(handleOrWallet.toLowerCase())}`;
}
function getAvatarFallback(display: string) {
  const firstChar = display.trim().charAt(0).toUpperCase();
  return <span className="tw-text-[0.6875rem] tw-font-semibold tw-text-iron-300">
      {firstChar || "?"}
    </span>;
}
function getDialogTitle({
  scope,
  category,
  contributorLabel
}: {
  readonly scope: RepContributorScope;
  readonly category?: string | null | undefined;
  readonly contributorLabel: string;
}) {
  if (scope === "category" && category) {
    return `${category} ${contributorLabel}`;
  }
  return `All ${contributorLabel}`;
}
function mapOverviewContributor(contributor: ApiRatingWithProfileInfoAndLevelWithPfp): RepContributorListItem {
  const handle = contributor.handle.trim();
  const firstWallet = contributor.wallets[0]?.trim();
  const fallbackWallet = firstWallet !== undefined && firstWallet.length > 0 ? firstWallet : contributor.handle;
  const display = handle.length > 0 ? handle : formatAddress(fallbackWallet);
  const routeValue = handle.length > 0 ? handle : fallbackWallet;
  return {
    key: contributor.profile_id,
    href: getRouteHref(routeValue),
    display,
    pfpUrl: contributor.pfp ?? null,
    contribution: contributor.rating
  };
}
function mapCategoryContributor(contributor: ApiRepContributor): RepContributorListItem {
  const fallbackWallet = contributor.profile.primary_address;
  const handle = contributor.profile.handle?.trim() ?? "";
  const routeValue = handle.length > 0 ? handle : fallbackWallet;
  const display = handle.length > 0 ? handle : formatAddress(fallbackWallet);
  return {
    key: contributor.profile.id,
    href: getRouteHref(routeValue),
    display,
    pfpUrl: contributor.profile.pfp ?? null,
    contribution: contributor.contribution
  };
}
function RepContributorRow(t0) {
  const $ = _c(18);
  const {
    contributor,
    showAvatar
  } = t0;
  const t1 = contributor.href;
  const t2 = `tw-flex tw-min-w-0 tw-items-center ${showAvatar ? "tw-gap-3" : "tw-gap-0"}`;
  let t3;
  if ($[0] !== contributor.display || $[1] !== contributor.pfpUrl || $[2] !== showAvatar) {
    t3 = showAvatar && <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-iron-900 tw-ring-1 tw-ring-white/10">{contributor.pfpUrl ? <img src={getScaledImageUri(contributor.pfpUrl, ImageScale.W_AUTO_H_50)} alt={`${contributor.display} profile`} className="tw-h-full tw-w-full tw-object-cover" /> : getAvatarFallback(contributor.display)}</div>;
    $[0] = contributor.display;
    $[1] = contributor.pfpUrl;
    $[2] = showAvatar;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== contributor.display) {
    t4 = <p className="tw-mb-0 tw-whitespace-nowrap tw-text-left tw-text-sm tw-font-medium tw-text-white">{contributor.display}</p>;
    $[4] = contributor.display;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== contributor.contribution) {
    t6 = formatNumberWithCommas(contributor.contribution);
    $[10] = contributor.contribution;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <span className="tw-text-sm tw-font-medium tw-text-iron-300 tw-transition-colors">{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== contributor.href || $[15] !== t5 || $[16] !== t7) {
    t8 = <Link href={t1} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.02] tw-px-3 tw-py-2.5 tw-no-underline tw-transition-colors hover:tw-border-white/15 hover:tw-bg-white/[0.04]">{t5}{t7}</Link>;
    $[14] = contributor.href;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
async function fetchRepContributorPage({
  identity,
  scope,
  direction,
  category,
  pageParam
}: {
  readonly identity: string;
  readonly scope: RepContributorScope;
  readonly direction: RepDirection;
  readonly category: string | null;
  readonly pageParam: number;
}): Promise<RepContributorPage> {
  if (scope === "overview") {
    const page = await commonApiFetch<ApiRatingWithProfileInfoAndLevelPageWithPfp>({
      endpoint: `profiles/${identity}/rep/ratings/by-rater`,
      params: {
        given: direction === "given" ? "true" : "false",
        page: pageParam.toString(),
        page_size: REP_CONTRIBUTORS_PAGE_SIZE.toString(),
        order: "DESC",
        order_by: "rating"
      }
    });
    return {
      data: page.data.map(mapOverviewContributor),
      page: page.page,
      next: page.next
    };
  }
  if (!category) {
    throw new Error("Missing rep category.");
  }
  const page = await commonApiFetch<ApiRepContributorsPage>({
    endpoint: `profiles/${identity}/rep/categories/${encodeURIComponent(category)}/contributors`,
    params: {
      ...(direction === "given" ? {
        direction: "outgoing"
      } : {}),
      page: pageParam.toString(),
      page_size: REP_CONTRIBUTORS_PAGE_SIZE.toString()
    }
  });
  return {
    data: page.data.map(mapCategoryContributor),
    page: page.page,
    next: page.next
  };
}
export default function RepContributorsDialog(t0) {
  const $ = _c(46);
  const {
    identity,
    isOpen,
    scope,
    direction,
    category,
    contributorCount,
    onClose
  } = t0;
  const trimmedCategory = category?.trim();
  let normalizedCategory = trimmedCategory ?? null;
  if (normalizedCategory === "") {
    normalizedCategory = null;
  }
  const contributorsQuery = useInfiniteQuery({
    queryKey: [REP_CONTRIBUTORS_QUERY_KEY, {
      identity,
      scope,
      direction,
      category: normalizedCategory
    }],
    queryFn: async t1 => {
      const {
        pageParam
      } = t1;
      return await fetchRepContributorPage({
        identity,
        scope,
        direction,
        category: normalizedCategory,
        pageParam
      });
    },
    enabled: isOpen && (scope === "overview" || normalizedCategory !== null),
    initialPageParam: 1,
    staleTime: REP_CONTRIBUTORS_STALE_TIME_MS,
    getNextPageParam: _temp
  });
  const contributorLabel = getContributorLabel(direction, contributorCount);
  const title = getDialogTitle({
    scope,
    category: normalizedCategory,
    contributorLabel
  });
  contributorsQuery.data?.pages;
  let t2;
  if ($[0] !== contributorsQuery.data?.pages) {
    t2 = contributorsQuery.data?.pages.flatMap(_temp2) ?? [];
    $[0] = contributorsQuery.data?.pages;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const items = t2;
  const showErrorState = contributorsQuery.isLoadingError;
  const showLoadMoreError = contributorsQuery.isFetchNextPageError;
  const errorMessage = contributorsQuery.error instanceof Error ? contributorsQuery.error.message : "Failed to load contributors.";
  let t3;
  if ($[2] !== contributorsQuery) {
    t3 = () => {
      contributorsQuery.fetchNextPage().catch(_temp3);
    };
    $[2] = contributorsQuery;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const fetchNextPage = t3;
  let t4;
  if ($[4] !== contributorsQuery) {
    t4 = () => {
      contributorsQuery.refetch().catch(_temp4);
    };
    $[4] = contributorsQuery;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const refetchContributors = t4;
  let t5;
  if ($[6] !== contributorsQuery.hasNextPage || $[7] !== contributorsQuery.isFetchNextPageError || $[8] !== contributorsQuery.isFetching || $[9] !== contributorsQuery.isPending || $[10] !== fetchNextPage) {
    t5 = state => {
      if (!state) {
        return;
      }
      if (contributorsQuery.isPending || contributorsQuery.isFetching) {
        return;
      }
      if (contributorsQuery.isFetchNextPageError) {
        return;
      }
      if (!contributorsQuery.hasNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[6] = contributorsQuery.hasNextPage;
    $[7] = contributorsQuery.isFetchNextPageError;
    $[8] = contributorsQuery.isFetching;
    $[9] = contributorsQuery.isPending;
    $[10] = fetchNextPage;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const onBottomIntersection = t5;
  let t6;
  if ($[12] !== contributorCount) {
    t6 = formatNumberWithCommas(contributorCount);
    $[12] = contributorCount;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== contributorLabel || $[15] !== t6) {
    t7 = <div className="tw-mb-4 tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-3"><p className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-500">{t6} {contributorLabel}</p></div>;
    $[14] = contributorLabel;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== contributorsQuery.isPending) {
    t8 = contributorsQuery.isPending && <div className="tw-flex tw-justify-center tw-py-12"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
    $[17] = contributorsQuery.isPending;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== errorMessage || $[20] !== refetchContributors || $[21] !== showErrorState) {
    t9 = showErrorState && <div className="tw-border-red-500/20 tw-bg-red-500/5 tw-rounded-xl tw-border tw-border-solid tw-p-4"><p className="tw-mb-0 tw-text-sm tw-text-iron-300">{errorMessage}</p><button type="button" onClick={refetchContributors} className="tw-mt-3 tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-white/[0.03] tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors hover:tw-border-white/15 hover:tw-bg-white/[0.06]">Retry</button></div>;
    $[19] = errorMessage;
    $[20] = refetchContributors;
    $[21] = showErrorState;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== contributorLabel || $[24] !== contributorsQuery.status || $[25] !== items.length) {
    t10 = contributorsQuery.status === "success" && items.length === 0 && <div className="tw-border-white/8 tw-rounded-xl tw-border tw-border-solid tw-bg-white/[0.02] tw-p-4"><p className="tw-mb-0 tw-text-sm tw-text-iron-400">No {contributorLabel} found.</p></div>;
    $[23] = contributorLabel;
    $[24] = contributorsQuery.status;
    $[25] = items.length;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== contributorLabel || $[28] !== contributorsQuery.isFetchingNextPage || $[29] !== fetchNextPage || $[30] !== items || $[31] !== onBottomIntersection || $[32] !== scope || $[33] !== showLoadMoreError) {
    t11 = items.length > 0 && <div className="tw-flex tw-flex-col tw-gap-3">{items.map(contributor => <RepContributorRow key={contributor.key} contributor={contributor} showAvatar={scope === "category"} />)}{contributorsQuery.isFetchingNextPage && <div className="tw-flex tw-justify-center tw-py-4"><CircleLoader size={CircleLoaderSize.MEDIUM} /></div>}{showLoadMoreError && <div className="tw-border-red-500/15 tw-bg-red-500/[0.03] tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-px-3 tw-py-3"><p className="tw-mb-0 tw-text-xs tw-text-iron-400">Could not load more {contributorLabel}.</p><button type="button" onClick={fetchNextPage} className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-white tw-transition-colors hover:tw-text-iron-300">Retry</button></div>}<CommonIntersectionElement onIntersection={onBottomIntersection} /></div>;
    $[27] = contributorLabel;
    $[28] = contributorsQuery.isFetchingNextPage;
    $[29] = fetchNextPage;
    $[30] = items;
    $[31] = onBottomIntersection;
    $[32] = scope;
    $[33] = showLoadMoreError;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t12 = <div className="tw-px-4 sm:tw-px-6">{t7}{t8}{t9}{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== isOpen || $[42] !== onClose || $[43] !== t12 || $[44] !== title) {
    t13 = <MobileWrapperDialog title={title} isOpen={isOpen} onClose={onClose} tall={true} fixedHeight={true} tabletModal={true} maxWidthClass="md:tw-max-w-xl md:[&_h2]:tw-mt-2" showScrollbar={true}>{t12}</MobileWrapperDialog>;
    $[41] = isOpen;
    $[42] = onClose;
    $[43] = t12;
    $[44] = title;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}
function _temp4() {}
function _temp3() {}
function _temp2(page) {
  return page.data;
}
function _temp(lastPage) {
  return lastPage?.next ? lastPage.page + 1 : undefined;
}